/**
 * CRM API Configuration
 * =====================
 *
 * This file contains the API configuration for the Lead Intake system.
 * API endpoints and products are centralized in constants.js and products.js.
 */

import { API_ENDPOINTS } from './constants';
import { PRODUCTS } from './products';

// Re-export for backward compatibility
export { PRODUCTS };

const API_BASE_URL = API_ENDPOINTS.crm;

/**
 * Submit a new lead to the CRM system
 * @param {Object} leadData - The lead data to submit
 * @returns {Promise<Object>} - Response from API
 */
export const submitLead = async (leadData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/leads`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...leadData,
        source: 'website',
        timestamp: new Date().toISOString()
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to submit lead');
    }

    return await response.json();
  } catch (error) {
    console.error('Lead submission error:', error);
    throw error;
  }
};

/**
 * Validate email format
 * @param {string} email - Email to validate
 * @returns {boolean}
 */
export const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validate phone format (international)
 * @param {string} phone - Phone to validate
 * @returns {boolean}
 */
export const validatePhone = (phone) => {
  if (!phone) return true; // Phone is optional
  const phoneRegex = /^[+]?[(]?[0-9]{1,4}[)]?[-\s./0-9]*$/;
  return phoneRegex.test(phone) && phone.length >= 10;
};

export default {
  API_BASE_URL,
  submitLead,
  validateEmail,
  validatePhone,
  PRODUCTS,
};
